import React from 'react';

function Bone({ className = '', style, ...props }) {
  return <span className={`skeleton-bone ${className}`.trim()} style={style} aria-hidden {...props} />;
}

export function ProfileSkeleton() {
  return (
    <div className="skeleton-block" aria-busy="true" aria-label="Loading profile">
      <div className="profile-card-top">
        <Bone className="skeleton-avatar" />
        <div className="profile-info" style={{ width: '100%', alignItems: 'center', gap: 8 }}>
          <Bone style={{ width: '46%', height: 18 }} />
          <Bone style={{ width: '62%', height: 12 }} />
        </div>
      </div>
      <div className="profile-balance skeleton-balance">
        <Bone className="skeleton-icon-box" />
        <div className="profile-balance-text" style={{ flex: 1, gap: 6 }}>
          <Bone style={{ width: 72, height: 10 }} />
          <Bone style={{ width: 110, height: 22 }} />
        </div>
      </div>
    </div>
  );
}

export function BankAccountsSkeleton({ rows = 3 }) {
  return (
    <div className="skeleton-block" aria-busy="true" aria-label="Loading bank accounts">
      <div className="bank-account-list">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="bank-account-item skeleton-row">
            <Bone className="skeleton-icon-box" />
            <div className="bank-account-body" style={{ gap: 6 }}>
              <div className="bank-account-title-row">
                <Bone style={{ width: '55%', height: 14 }} />
                <Bone style={{ width: 42, height: 18, borderRadius: 999 }} />
              </div>
              <Bone style={{ width: '72%', height: 12 }} />
              <Bone style={{ width: '40%', height: 12 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MerchantListSkeleton({ rows = 4 }) {
  return (
    <div className="skeleton-block" aria-busy="true" aria-label="Loading merchants">
      <div className="merchant-pick-list">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="merchant-pick-card skeleton-row" style={{ pointerEvents: 'none' }}>
            <Bone className="skeleton-icon-box" style={{ width: 40, height: 40 }} />
            <div className="merchant-pick-body" style={{ gap: 6 }}>
              <Bone style={{ width: '58%', height: 14 }} />
              <Bone style={{ width: '76%', height: 12 }} />
            </div>
            <Bone style={{ width: 18, height: 18, borderRadius: 4 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function SellFormSkeleton() {
  return (
    <div className="skeleton-block" aria-busy="true" aria-label="Loading sell form">
      <Bone style={{ width: '40%', height: 16, marginBottom: 8 }} />
      <Bone style={{ width: '78%', height: 12, marginBottom: 14 }} />
      <div className="sell-balance-chip skeleton-balance">
        <Bone className="skeleton-icon-box" />
        <div className="sell-balance-text" style={{ flex: 1, gap: 6 }}>
          <Bone style={{ width: 90, height: 10 }} />
          <Bone style={{ width: 100, height: 20 }} />
        </div>
      </div>
      <div className="rate-summary" style={{ marginTop: 12 }}>
        <Bone style={{ width: '70%', height: 14 }} />
      </div>
      <div className="form" style={{ marginTop: 14, gap: 14 }}>
        <div className="form-group">
          <Bone style={{ width: 90, height: 12 }} />
          <Bone style={{ width: '100%', height: 42, borderRadius: 999 }} />
          <Bone style={{ width: '55%', height: 12 }} />
        </div>
        <div className="form-group">
          <Bone style={{ width: 80, height: 12 }} />
          <Bone style={{ width: '100%', height: 42, borderRadius: 999 }} />
        </div>
        <Bone style={{ width: '100%', height: 44, borderRadius: 12 }} />
      </div>
    </div>
  );
}

export function PaymentDetailsSkeleton() {
  return (
    <div className="skeleton-block" aria-busy="true" aria-label="Loading payment details">
      <Bone style={{ width: '44%', height: 16, marginBottom: 8 }} />
      <Bone style={{ width: '72%', height: 12, marginBottom: 14 }} />
      <div className="rate-summary" style={{ marginBottom: 12 }}>
        <Bone style={{ width: '66%', height: 14, marginBottom: 8 }} />
        <Bone style={{ width: '58%', height: 14 }} />
      </div>
      <Bone style={{ width: '100%', height: 42, borderRadius: 12, marginBottom: 12 }} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <Bone style={{ width: 160, height: 160, borderRadius: 12 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Bone style={{ width: 90, height: 32, borderRadius: 10 }} />
          <Bone style={{ width: 90, height: 32, borderRadius: 10 }} />
        </div>
      </div>
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="dashboard-page skeleton-block" aria-busy="true" aria-label="Loading dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="dashboard-hero">
        <Bone style={{ width: 72, height: 22, marginBottom: 8 }} />
        <Bone style={{ width: '78%', height: 12 }} />
      </div>
      <div className="stats-row dashboard-stats">
        {[0, 1].map((i) => (
          <div key={i} className="stat-card skeleton-row">
            <div className="stat-head">
              <Bone className="skeleton-icon-box" style={{ width: 30, height: 30, borderRadius: 12 }} />
              <Bone style={{ width: 70, height: 10 }} />
            </div>
            <Bone style={{ width: '55%', height: 18 }} />
          </div>
        ))}
      </div>
      <div className="card">
        <div className="card-title-row" style={{ marginBottom: 12 }}>
          <Bone style={{ width: 120, height: 16 }} />
          <Bone style={{ width: 80, height: 22, borderRadius: 999 }} />
        </div>
        <div className="skeleton-feed">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton-feed-item">
              <Bone style={{ width: '100%', height: 12 }} />
              <Bone style={{ width: '72%', height: 12 }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SupportSkeleton() {
  return (
    <div className="skeleton-block" aria-busy="true" aria-label="Loading support" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="support-telegram-card skeleton-row">
        <Bone style={{ width: 72, height: 72, borderRadius: '50%' }} />
        <Bone style={{ width: '60%', height: 18 }} />
        <Bone style={{ width: '80%', height: 12 }} />
        <Bone style={{ width: '70%', height: 12 }} />
        <Bone style={{ width: 180, height: 44, borderRadius: 12 }} />
      </div>
      <div className="support-quick-card">
        <Bone style={{ width: '90%', height: 12 }} />
        <Bone style={{ width: '70%', height: 12, marginTop: 8 }} />
      </div>
    </div>
  );
}

export function ReportListSkeleton({ rows = 4 }) {
  return (
    <div className="skeleton-block" aria-busy="true" aria-label="Loading report">
      <ul className="exchange-list">
        {Array.from({ length: rows }).map((_, i) => (
          <li key={i} className="exchange-item skeleton-row" style={{ pointerEvents: 'none' }}>
            <div className="exchange-item-main">
              <div className="exchange-item-amount" style={{ gap: 6 }}>
                <Bone style={{ width: 48, height: 10 }} />
                <Bone style={{ width: 72, height: 16 }} />
              </div>
              <div className="exchange-item-amount" style={{ gap: 6 }}>
                <Bone style={{ width: 36, height: 10 }} />
                <Bone style={{ width: 56, height: 16 }} />
              </div>
              <Bone style={{ width: 64, height: 20, borderRadius: 999 }} />
            </div>
            <div className="exchange-item-meta" style={{ gap: 8 }}>
              <Bone style={{ width: '48%', height: 12 }} />
              <Bone style={{ width: '40%', height: 12 }} />
            </div>
            <Bone style={{ width: '36%', height: 11, marginTop: 6 }} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TradeOptionsSkeleton() {
  return (
    <div className="card skeleton-block" aria-busy="true" aria-label="Loading trade options">
      <Bone style={{ width: '42%', height: 16, marginBottom: 8 }} />
      <Bone style={{ width: '78%', height: 12, marginBottom: 14 }} />
      <div className="trade-option-list">
        {[0, 1].map((i) => (
          <div key={i} className="trade-option-card skeleton-row" style={{ pointerEvents: 'none' }}>
            <Bone className="skeleton-icon-box" style={{ width: 44, height: 44, borderRadius: 12 }} />
            <div className="trade-option-body" style={{ gap: 6 }}>
              <Bone style={{ width: '30%', height: 16 }} />
              <Bone style={{ width: '70%', height: 12 }} />
            </div>
            <Bone style={{ width: 18, height: 18 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export { Bone };
export default Bone;
